import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { dealerEventsApi, ordersApi, dealerLeadsApi, companyEventsApi } from '../services/api';
import { AddToCalendarButton } from '../components/AddToCalendarButton';

interface CalendarEvent {
  id: string;
  type: 'install' | 'follow_up' | 'site_visit' | 'measure' | 'quote_presentation' | 'meeting' | 'other' | 'holiday' | 'dealer_meeting' | 'training' | 'announcement' | 'company_event';
  title: string;
  date: string;
  time?: string;
  notes?: string;
  relatedOrderId?: string;
  relatedLeadId?: string;
  location?: string;
  durationMinutes?: number;
  // Install-specific
  orderNumber?: string;
  sidemark?: string;
  status?: string;
  totalAmount?: number;
  // Lead-specific
  activityType?: string;
  customerName?: string;
  leadStatus?: string;
  // Company event specific
  isCompanyEvent?: boolean;
}

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: CalendarEvent[];
}

interface AddEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (event: any) => void;
  initialDate?: Date;
}

const eventTypeColors: Record<string, string> = {
  install: 'bg-green-500',
  follow_up: 'bg-blue-500',
  site_visit: 'bg-purple-500',
  measure: 'bg-purple-500',
  quote_presentation: 'bg-purple-500',
  meeting: 'bg-purple-500',
  other: 'bg-purple-500',
  // Company event types
  holiday: 'bg-red-600',
  dealer_meeting: 'bg-yellow-500',
  training: 'bg-cyan-500',
  announcement: 'bg-orange-500',
  company_event: 'bg-indigo-500',
};

const eventTypeIcons: Record<string, string> = {
  install: '🔧',
  follow_up: '📞',
  site_visit: '📍',
  measure: '📏',
  quote_presentation: '📊',
  meeting: '🤝',
  other: '📌',
  // Company event types
  holiday: '🎉',
  dealer_meeting: '🏢',
  training: '📚',
  announcement: '📢',
  company_event: '🏢',
};

const eventTypeLabels: Record<string, string> = {
  install: 'Install',
  follow_up: 'Follow-up',
  site_visit: 'Site Visit',
  measure: 'Measure',
  quote_presentation: 'Quote Presentation',
  meeting: 'Meeting',
  other: 'Other',
  // Company event types
  holiday: 'Company Holiday',
  dealer_meeting: 'Meeting with Keder',
  training: 'Training',
  announcement: 'Announcement',
  company_event: 'Company Event',
};

function AddEventModal({ isOpen, onClose, onSave, initialDate }: AddEventModalProps) {
  const [formData, setFormData] = useState({
    eventType: 'site_visit',
    title: '',
    eventDate: initialDate ? initialDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    eventTime: '',
    durationMinutes: 60,
    location: '',
    relatedLeadId: '',
    relatedOrderId: '',
    notes: '',
    reminder: 'none',
  });

  const [leads, setLeads] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen) {
      // Fetch active leads
      dealerLeadsApi.list({ pageSize: 100 }).then(res => setLeads(Array.isArray(res) ? res : (res.data || []))).catch(() => setLeads([]));
      // Fetch recent orders
      ordersApi.list({ limit: 50 }).then(res => setOrders(res.items || [])).catch(() => setOrders([]));
    }
  }, [isOpen]);

  useEffect(() => {
    if (initialDate) {
      setFormData(prev => ({ ...prev, eventDate: initialDate.toISOString().split('T')[0] }));
    }
  }, [initialDate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      durationMinutes: formData.durationMinutes ? parseInt(String(formData.durationMinutes)) : undefined,
      relatedLeadId: formData.relatedLeadId || undefined,
      relatedOrderId: formData.relatedOrderId || undefined,
    });
    onClose();
    // Reset form
    setFormData({
      eventType: 'site_visit',
      title: '',
      eventDate: new Date().toISOString().split('T')[0],
      eventTime: '',
      durationMinutes: 60,
      location: '',
      relatedLeadId: '',
      relatedOrderId: '',
      notes: '',
      reminder: 'none',
    });
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        /* Keder Yellow Date/Time Picker Styling */
        input[type="date"]::-webkit-calendar-picker-indicator,
        input[type="time"]::-webkit-calendar-picker-indicator {
          filter: invert(85%) sepia(65%) saturate(2000%) hue-rotate(360deg) brightness(105%);
          cursor: pointer;
        }

        input[type="date"]:focus::-webkit-calendar-picker-indicator,
        input[type="time"]:focus::-webkit-calendar-picker-indicator {
          filter: invert(75%) sepia(75%) saturate(2500%) hue-rotate(360deg) brightness(110%);
        }
      `}</style>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-keder-dark border border-gray-700 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-keder-dark z-10">
            <h3 className="text-lg font-semibold text-white">Add Event</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Event Type */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Event Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.eventType}
              onChange={(e) => setFormData({ ...formData, eventType: e.target.value })}
              className="w-full px-3 py-2 bg-keder-gray border border-gray-600 rounded text-white focus:ring-2 focus:ring-keder-yellow focus:border-transparent"
              required
            >
              <option value="site_visit">Site Visit</option>
              <option value="measure">Measure</option>
              <option value="quote_presentation">Quote Presentation</option>
              <option value="meeting">Meeting</option>
              <option value="follow_up">Follow-up</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 bg-keder-gray border border-gray-600 rounded text-white focus:ring-2 focus:ring-keder-yellow focus:border-transparent"
              placeholder="Meeting with John Smith"
              required
            />
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.eventDate}
                onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                className="w-full px-3 py-2 bg-keder-gray border border-gray-600 rounded text-white focus:ring-2 focus:ring-keder-yellow focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Time</label>
              <input
                type="time"
                value={formData.eventTime}
                onChange={(e) => setFormData({ ...formData, eventTime: e.target.value })}
                className="w-full px-3 py-2 bg-keder-gray border border-gray-600 rounded text-white focus:ring-2 focus:ring-keder-yellow focus:border-transparent"
              />
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">Duration</label>
            <select
              value={formData.durationMinutes}
              onChange={(e) => setFormData({ ...formData, durationMinutes: parseInt(e.target.value) })}
              className="w-full px-3 py-2 bg-keder-gray border border-gray-600 rounded text-white focus:ring-2 focus:ring-keder-yellow focus:border-transparent"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={60}>1 hour</option>
              <option value={120}>2 hours</option>
              <option value={240}>Half Day</option>
              <option value={480}>All Day</option>
            </select>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="w-full px-3 py-2 bg-keder-gray border border-gray-600 rounded text-white focus:ring-2 focus:ring-keder-yellow focus:border-transparent"
              placeholder="123 Main St, Dallas TX"
            />
          </div>

          {/* Related To */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Link to Lead</label>
              <select
                value={formData.relatedLeadId}
                onChange={(e) => setFormData({ ...formData, relatedLeadId: e.target.value })}
                className="w-full px-3 py-2 bg-keder-gray border border-gray-600 rounded text-white focus:ring-2 focus:ring-keder-yellow focus:border-transparent"
              >
                <option value="">None</option>
                {leads.map(lead => (
                  <option key={lead.id} value={lead.id}>{lead.customerName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Link to Order</label>
              <select
                value={formData.relatedOrderId}
                onChange={(e) => setFormData({ ...formData, relatedOrderId: e.target.value })}
                className="w-full px-3 py-2 bg-keder-gray border border-gray-600 rounded text-white focus:ring-2 focus:ring-keder-yellow focus:border-transparent"
              >
                <option value="">None</option>
                {orders.map(order => (
                  <option key={order.id} value={order.id}>{order.orderNumber} - {order.sidemark || 'No sidemark'}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 bg-keder-gray border border-gray-600 rounded text-white focus:ring-2 focus:ring-keder-yellow focus:border-transparent"
              rows={3}
              placeholder="Additional details..."
            />
          </div>

          {/* Reminder */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">Reminder</label>
            <select
              value={formData.reminder}
              onChange={(e) => setFormData({ ...formData, reminder: e.target.value })}
              className="w-full px-3 py-2 bg-keder-gray border border-gray-600 rounded text-white focus:ring-2 focus:ring-keder-yellow focus:border-transparent"
            >
              <option value="none">None</option>
              <option value="15min">15 minutes before</option>
              <option value="1hr">1 hour before</option>
              <option value="1day">1 day before</option>
              <option value="1week">1 week before</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-keder-yellow hover:bg-yellow-500 text-black font-semibold rounded transition-colors"
            >
              Add Event
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
}

export function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [allEvents, setAllEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);
  const [isAddEventModalOpen, setIsAddEventModalOpen] = useState(false);
  const [quickAddDate, setQuickAddDate] = useState<Date | undefined>(undefined);
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');

  // Event type filters
  const [filters, setFilters] = useState({
    installs: true,
    followUps: true,
    customEvents: true,
    companyEvents: true,
  });

  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  useEffect(() => {
    fetchCalendarData();
  }, [currentMonth, currentYear]);

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch both dealer events and company events in parallel
      const startDate = new Date(currentYear, currentMonth - 1, 1).toISOString();
      const endDate = new Date(currentYear, currentMonth, 0, 23, 59, 59).toISOString();

      const [data, companyEvents] = await Promise.all([
        dealerEventsApi.getCalendar(currentMonth, currentYear),
        companyEventsApi.getDealerEvents(startDate, endDate).catch(() => []),
      ]);

      // Convert company events to CalendarEvent format
      const companyCalendarEvents: CalendarEvent[] = (companyEvents || []).map((event: any) => ({
        id: event.id,
        type: event.eventType || 'company_event',
        title: event.title,
        date: event.startTime,
        time: event.allDay ? undefined : new Date(event.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        notes: event.description,
        location: event.location,
        isCompanyEvent: true,
      }));

      // Merge all event types (with null checks to prevent spread errors)
      const events: CalendarEvent[] = [
        ...(data.installEvents || []),
        ...(data.followUpEvents || []),
        ...(data.customEvents || []),
        ...companyCalendarEvents,
      ];

      setAllEvents(events);
    } catch (err: any) {
      setError(err.message || 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = async (eventData: any) => {
    try {
      await dealerEventsApi.create(eventData);
      await fetchCalendarData();
    } catch (err: any) {
      alert('Failed to create event: ' + (err.message || 'Unknown error'));
    }
  };

  // Filter events based on active filters
  const filteredEvents = useMemo(() => {
    return allEvents.filter(event => {
      if (event.type === 'install' && !filters.installs) return false;
      if (event.type === 'follow_up' && !filters.followUps) return false;
      if (['site_visit', 'measure', 'quote_presentation', 'meeting', 'other'].includes(event.type) && !event.isCompanyEvent && !filters.customEvents) return false;
      if (event.isCompanyEvent && !filters.companyEvents) return false;
      return true;
    });
  }, [allEvents, filters]);

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const days: CalendarDay[] = [];
    const firstDay = new Date(currentYear, currentDate.getMonth(), 1);
    const lastDay = new Date(currentYear, currentDate.getMonth() + 1, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Days from previous month
    const firstDayOfWeek = firstDay.getDay();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(firstDay);
      date.setDate(date.getDate() - i - 1);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: false,
        events: [],
      });
    }

    // Days of current month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(currentYear, currentDate.getMonth(), d);
      const dateStr = date.toISOString().split('T')[0];

      // Find events for this day
      const dayEvents = filteredEvents.filter(event => {
        if (!event.date) return false;
        const eventDateObj = new Date(event.date);
        if (isNaN(eventDateObj.getTime())) return false;
        const eventDate = eventDateObj.toISOString().split('T')[0];
        return eventDate === dateStr;
      });

      days.push({
        date,
        isCurrentMonth: true,
        isToday: date.getTime() === today.getTime(),
        events: dayEvents,
      });
    }

    // Days from next month
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(lastDay);
      date.setDate(date.getDate() + i);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: false,
        events: [],
      });
    }

    return days;
  }, [currentDate, filteredEvents, currentYear]);

  // Generate week days for week view
  const weekDays = useMemo(() => {
    const days: CalendarDay[] = [];
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];

      const dayEvents = filteredEvents.filter(event => {
        if (!event.date) return false;
        const eventDateObj = new Date(event.date);
        if (isNaN(eventDateObj.getTime())) return false;
        const eventDate = eventDateObj.toISOString().split('T')[0];
        return eventDate === dateStr;
      });

      days.push({
        date,
        isCurrentMonth: date.getMonth() === currentDate.getMonth(),
        isToday: date.getTime() === today.getTime(),
        events: dayEvents,
      });
    }

    return days;
  }, [currentDate, filteredEvents]);

  // Get events for current day
  const dayEvents = useMemo(() => {
    const dateStr = currentDate.toISOString().split('T')[0];
    return filteredEvents
      .filter(event => {
        if (!event.date) return false;
        const eventDateObj = new Date(event.date);
        if (isNaN(eventDateObj.getTime())) return false;
        const eventDate = eventDateObj.toISOString().split('T')[0];
        return eventDate === dateStr;
      })
      .sort((a, b) => {
        if (!a.time && !b.time) return 0;
        if (!a.time) return 1;
        if (!b.time) return -1;
        return a.time.localeCompare(b.time);
      });
  }, [currentDate, filteredEvents]);

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentYear, currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const goToPrevious = () => {
    if (viewMode === 'month') {
      goToPreviousMonth();
    } else if (viewMode === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() - 7);
      setCurrentDate(newDate);
    } else if (viewMode === 'day') {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() - 1);
      setCurrentDate(newDate);
    }
  };

  const goToNext = () => {
    if (viewMode === 'month') {
      goToNextMonth();
    } else if (viewMode === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + 7);
      setCurrentDate(newDate);
    } else if (viewMode === 'day') {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + 1);
      setCurrentDate(newDate);
    }
  };

  const getEventColor = (event: CalendarEvent) => {
    // Overdue follow-ups in red
    if (event.type === 'follow_up' && event.date) {
      const eventDate = new Date(event.date);
      if (!isNaN(eventDate.getTime())) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        eventDate.setHours(0, 0, 0, 0);
        if (eventDate < today) {
          return 'bg-red-500';
        }
      }
    }
    return eventTypeColors[event.type] || 'bg-gray-500';
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Upcoming events
  const upcomingEvents = filteredEvents
    .filter(event => {
      if (!event.date) return false;
      const eventDate = new Date(event.date);
      if (isNaN(eventDate.getTime())) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return eventDate >= today;
    })
    .sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    })
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Calendar</h1>
          <p className="text-gray-400">Manage your installs, leads, and events</p>
        </div>
        <button
          onClick={() => {
            setQuickAddDate(undefined);
            setIsAddEventModalOpen(true);
          }}
          className="bg-keder-yellow hover:bg-yellow-500 text-black font-semibold px-6 py-2 rounded-lg transition-colors"
        >
          + Add Event
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-3 card">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">
              {viewMode === 'month' && `${monthNames[currentDate.getMonth()]} ${currentYear}`}
              {viewMode === 'week' && (() => {
                const start = weekDays[0]?.date;
                const end = weekDays[6]?.date;
                if (!start || !end) return '';
                const startMonth = monthNames[start.getMonth()];
                const endMonth = monthNames[end.getMonth()];
                const startYear = start.getFullYear();
                const endYear = end.getFullYear();
                if (startMonth === endMonth && startYear === endYear) {
                  return `${startMonth} ${start.getDate()} - ${end.getDate()}, ${startYear}`;
                } else if (startYear === endYear) {
                  return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}, ${startYear}`;
                } else {
                  return `${startMonth} ${start.getDate()}, ${startYear} - ${endMonth} ${end.getDate()}, ${endYear}`;
                }
              })()}
              {viewMode === 'day' && currentDate.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </h2>
            <div className="flex items-center gap-2">
              {/* View Toggle Buttons */}
              <div className="flex items-center bg-keder-gray rounded overflow-hidden">
                <button
                  onClick={() => setViewMode('month')}
                  className={`px-3 py-1 text-sm font-medium transition-colors ${
                    viewMode === 'month'
                      ? 'bg-keder-yellow text-black'
                      : 'text-white hover:bg-gray-600'
                  }`}
                >
                  Month
                </button>
                <button
                  onClick={() => setViewMode('week')}
                  className={`px-3 py-1 text-sm font-medium transition-colors ${
                    viewMode === 'week'
                      ? 'bg-keder-yellow text-black'
                      : 'text-white hover:bg-gray-600'
                  }`}
                >
                  Week
                </button>
                <button
                  onClick={() => setViewMode('day')}
                  className={`px-3 py-1 text-sm font-medium transition-colors ${
                    viewMode === 'day'
                      ? 'bg-keder-yellow text-black'
                      : 'text-white hover:bg-gray-600'
                  }`}
                >
                  Day
                </button>
              </div>

              <button
                onClick={goToToday}
                className="px-3 py-1 text-sm bg-keder-gray hover:bg-gray-600 text-white rounded transition-colors"
              >
                Today
              </button>
              <button
                onClick={goToPrevious}
                className="p-2 bg-keder-gray hover:bg-gray-600 text-white rounded transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={goToNext}
                className="p-2 bg-keder-gray hover:bg-gray-600 text-white rounded transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-4 pb-4 border-b border-gray-700">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.installs}
                onChange={(e) => setFilters({ ...filters, installs: e.target.checked })}
                className="w-4 h-4 rounded border-gray-600 text-green-500 focus:ring-green-500 focus:ring-offset-keder-dark"
              />
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-green-500"></div>
                <span className="text-sm text-gray-300">Installs</span>
              </div>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.followUps}
                onChange={(e) => setFilters({ ...filters, followUps: e.target.checked })}
                className="w-4 h-4 rounded border-gray-600 text-blue-500 focus:ring-blue-500 focus:ring-offset-keder-dark"
              />
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-blue-500"></div>
                <span className="text-sm text-gray-300">Follow-ups</span>
              </div>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.customEvents}
                onChange={(e) => setFilters({ ...filters, customEvents: e.target.checked })}
                className="w-4 h-4 rounded border-gray-600 text-purple-500 focus:ring-purple-500 focus:ring-offset-keder-dark"
              />
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-purple-500"></div>
                <span className="text-sm text-gray-300">Custom Events</span>
              </div>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.companyEvents}
                onChange={(e) => setFilters({ ...filters, companyEvents: e.target.checked })}
                className="w-4 h-4 rounded border-gray-600 text-yellow-500 focus:ring-yellow-500 focus:ring-offset-keder-dark"
              />
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-yellow-500"></div>
                <span className="text-sm text-gray-300">Keder Events</span>
              </div>
            </label>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-keder-yellow"></div>
            </div>
          ) : (
            <>
              {/* Month View */}
              {viewMode === 'month' && (
                <>
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="text-center text-sm font-medium text-gray-400 py-2">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {calendarDays.map((day, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          if (day.isCurrentMonth) {
                            setSelectedDay(day);
                          }
                        }}
                        onDoubleClick={() => {
                          if (day.isCurrentMonth) {
                            setQuickAddDate(day.date);
                            setIsAddEventModalOpen(true);
                          }
                        }}
                        className={`
                          min-h-[80px] p-1 border rounded cursor-pointer transition-colors
                          ${day.isCurrentMonth ? 'border-gray-700 bg-keder-gray/30' : 'border-gray-800 bg-keder-gray/10'}
                          ${day.isToday ? 'ring-2 ring-keder-yellow' : ''}
                          ${day.isCurrentMonth ? 'hover:bg-keder-gray/50' : ''}
                        `}
                      >
                        <div className={`text-sm font-medium mb-1 ${day.isCurrentMonth ? 'text-white' : 'text-gray-600'}`}>
                          {day.date.getDate()}
                        </div>
                        <div className="space-y-1">
                          {day.events.slice(0, 2).map((event, i) => (
                            <div
                              key={i}
                              className={`text-xs px-1 py-0.5 rounded truncate text-white ${getEventColor(event)}`}
                              title={`${eventTypeIcons[event.type]} ${event.title}`}
                            >
                              <span className="w-1 h-1 rounded-full bg-keder-yellow inline-block mr-1"></span>
                              {eventTypeIcons[event.type]} {event.title}
                            </div>
                          ))}
                          {day.events.length > 2 && (
                            <div className="text-xs text-gray-400 text-center">
                              +{day.events.length - 2} more
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Week View */}
              {viewMode === 'week' && (
                <>
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-2 mb-2">
                    {weekDays.map((day, index) => (
                      <div key={index} className="text-center">
                        <div className="text-sm font-medium text-gray-400">
                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][index]}
                        </div>
                        <div className={`text-lg font-semibold mt-1 ${day.isToday ? 'text-keder-yellow' : 'text-white'}`}>
                          {day.date.getDate()}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Week Grid */}
                  <div className="grid grid-cols-7 gap-2">
                    {weekDays.map((day, index) => (
                      <div
                        key={index}
                        onClick={() => setSelectedDay(day)}
                        onDoubleClick={() => {
                          setQuickAddDate(day.date);
                          setIsAddEventModalOpen(true);
                        }}
                        className={`
                          min-h-[400px] p-2 border rounded cursor-pointer transition-colors
                          ${day.isToday ? 'border-keder-yellow bg-keder-gray/30' : 'border-gray-700 bg-keder-gray/20'}
                          hover:bg-keder-gray/40
                        `}
                      >
                        <div className="space-y-2">
                          {day.events.map((event, i) => (
                            <div
                              key={i}
                              className={`text-xs px-2 py-1.5 rounded text-white ${getEventColor(event)}`}
                            >
                              <div className="font-medium truncate">
                                <span className="w-1.5 h-1.5 rounded-full bg-keder-yellow inline-block mr-1"></span>
                                {event.time || 'All day'}
                              </div>
                              <div className="truncate">{eventTypeIcons[event.type]} {event.title}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Day View */}
              {viewMode === 'day' && (
                <div className="space-y-3">
                  {dayEvents.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-400 mb-4">No events scheduled for this day</p>
                      <button
                        onClick={() => {
                          setQuickAddDate(currentDate);
                          setIsAddEventModalOpen(true);
                        }}
                        className="px-4 py-2 bg-keder-yellow hover:bg-yellow-500 text-black font-semibold rounded transition-colors"
                      >
                        + Add Event
                      </button>
                    </div>
                  ) : (
                    <>
                      {dayEvents.map((event) => (
                        <div
                          key={event.id}
                          onClick={() => {
                            const dayData: CalendarDay = {
                              date: currentDate,
                              isCurrentMonth: true,
                              isToday: currentDate.toISOString().split('T')[0] === new Date().toISOString().split('T')[0],
                              events: dayEvents,
                            };
                            setSelectedDay(dayData);
                          }}
                          className="p-4 bg-keder-gray/30 border border-gray-700 rounded-lg hover:bg-keder-gray/50 cursor-pointer transition-colors"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="text-white font-medium text-lg flex items-center gap-2">
                                <span className={`w-3 h-3 rounded-full ${getEventColor(event)}`}></span>
                                <span>{eventTypeIcons[event.type]}</span>
                                <span>{event.title}</span>
                              </div>
                              <div className="text-sm text-gray-400 mt-1">{eventTypeLabels[event.type]}</div>
                              {event.location && (
                                <div className="text-sm text-gray-400 mt-1">
                                  📍 {event.location}
                                </div>
                              )}
                              {event.notes && (
                                <div className="text-sm text-gray-300 mt-2">
                                  {event.notes}
                                </div>
                              )}
                            </div>
                            <div className="text-right ml-4">
                              <div className={`px-3 py-1 text-sm font-medium rounded text-white ${getEventColor(event)}`}>
                                {event.time || 'All day'}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          setQuickAddDate(currentDate);
                          setIsAddEventModalOpen(true);
                        }}
                        className="w-full px-4 py-2 bg-keder-yellow hover:bg-yellow-500 text-black font-semibold rounded transition-colors"
                      >
                        + Add Event
                      </button>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Stats */}
          <div className="card">
            <h3 className="text-lg font-semibold text-white mb-4">This Month</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Installs</span>
                <span className="text-white font-medium">
                  {allEvents.filter(e => e.type === 'install').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Follow-ups</span>
                <span className="text-white font-medium">
                  {allEvents.filter(e => e.type === 'follow_up').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Other Events</span>
                <span className="text-white font-medium">
                  {allEvents.filter(e => !['install', 'follow_up'].includes(e.type)).length}
                </span>
              </div>
            </div>
          </div>

          {/* Upcoming Events */}
          <div className="card">
            <h3 className="text-lg font-semibold text-white mb-4">Upcoming</h3>
            {upcomingEvents.length === 0 ? (
              <p className="text-gray-400 text-sm">No upcoming events</p>
            ) : (
              <div className="space-y-3">
                {upcomingEvents.map(event => (
                  <div
                    key={event.id}
                    className="p-3 bg-keder-gray/50 rounded-lg hover:bg-keder-gray/70 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="text-white font-medium flex items-center gap-2">
                          <span>{eventTypeIcons[event.type]}</span>
                          <span className="truncate">{event.title}</span>
                        </div>
                        {event.orderNumber && (
                          <div className="text-sm text-keder-yellow">{event.orderNumber}</div>
                        )}
                        {event.customerName && (
                          <div className="text-sm text-gray-400">{event.customerName}</div>
                        )}
                      </div>
                      <div className="text-right ml-2">
                        <div className="text-sm text-white whitespace-nowrap">
                          {event.date && !isNaN(new Date(event.date).getTime())
                            ? new Date(event.date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                              })
                            : '—'}
                        </div>
                        {event.time && (
                          <div className="text-xs text-gray-400">{event.time}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Selected Day Modal */}
      {selectedDay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-keder-dark border border-gray-700 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white">
                {selectedDay.date.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </h3>
              <button
                onClick={() => setSelectedDay(null)}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {selectedDay.events.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-400 mb-4">No events for this day</p>
                  <button
                    onClick={() => {
                      setQuickAddDate(selectedDay.date);
                      setSelectedDay(null);
                      setIsAddEventModalOpen(true);
                    }}
                    className="px-4 py-2 bg-keder-yellow hover:bg-yellow-500 text-black font-semibold rounded transition-colors"
                  >
                    + Add Event
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedDay.events.map(event => (
                    <div
                      key={event.id}
                      className="p-4 bg-keder-gray/50 rounded-lg"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="text-white font-medium text-lg flex items-center gap-2">
                            <span>{eventTypeIcons[event.type]}</span>
                            <span>{event.title}</span>
                          </div>
                          <div className="text-sm text-gray-400">{eventTypeLabels[event.type]}</div>
                        </div>
                        <span className={`px-3 py-1 text-xs font-medium rounded text-white ${getEventColor(event)}`}>
                          {event.time || 'All day'}
                        </span>
                      </div>
                      {event.location && (
                        <div className="text-sm text-gray-400 mb-1">
                          📍 {event.location}
                        </div>
                      )}
                      {event.notes && (
                        <div className="text-sm text-gray-300 mt-2 pt-2 border-t border-gray-600">
                          {event.notes}
                        </div>
                      )}

                      {/* Footer actions */}
                      <div className="mt-3 pt-3 border-t border-gray-600 flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                          {event.relatedOrderId && (
                            <Link
                              to={`/orders/${event.relatedOrderId}`}
                              className="text-sm text-keder-yellow hover:underline"
                              onClick={() => setSelectedDay(null)}
                            >
                              View Order →
                            </Link>
                          )}
                          {event.relatedLeadId && (
                            <Link
                              to={`/leads`}
                              className="text-sm text-blue-400 hover:underline"
                              onClick={() => setSelectedDay(null)}
                            >
                              View Lead →
                            </Link>
                          )}
                        </div>

                        {/* Add to Calendar button - only for install and custom events */}
                        {(event.type === 'install' || event.type === 'custom') && (
                          <AddToCalendarButton
                            eventType={event.type as 'install' | 'custom'}
                            eventId={event.id}
                            className="text-xs"
                          />
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Add Event button at bottom */}
                  <div className="pt-2 border-t border-gray-700">
                    <button
                      onClick={() => {
                        setQuickAddDate(selectedDay.date);
                        setSelectedDay(null);
                        setIsAddEventModalOpen(true);
                      }}
                      className="w-full px-4 py-2 bg-keder-yellow hover:bg-yellow-500 text-black font-semibold rounded transition-colors"
                    >
                      + Add Event
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Event Modal */}
      <AddEventModal
        isOpen={isAddEventModalOpen}
        onClose={() => {
          setIsAddEventModalOpen(false);
          setQuickAddDate(undefined);
        }}
        onSave={handleAddEvent}
        initialDate={quickAddDate}
      />
    </div>
  );
}

export default CalendarPage;
